import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BRAND, ROLE_LABELS, feedbackCategoryLabel } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, ErrorNote, SectionTitle, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { AdminOverview } from '@/types';
import { DELIVERY_CHANNEL_LABELS, formatCount } from './lib';

/** 서비스 관제 (§3.2) — 플랫폼 전체가 지금 건강한지 한 화면에서 본다 */
export function AdminHomePage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => apiData<AdminOverview>('/admin/overview'),
    refetchInterval: 30_000,
  });

  return (
    <Screen>
      <AppBar title="서비스 관제" subtitle={`${BRAND.name} 운영 콘솔`} />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="관제 정보를 불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && (
        <div className="flex flex-col gap-4 p-4">
          {/* 승인 대기가 있으면 가장 먼저 보여야 한다 */}
          {data.facilities.pending > 0 && (
            <Link to="/facilities?status=PENDING">
              <Card className="bg-accent-soft">
                <p className="font-semibold">승인 대기 기관 {data.facilities.pending}곳</p>
                <p className="mt-0.5 text-sm text-ink-sub">기관 탭에서 확인하고 승인해 주세요.</p>
              </Card>
            </Link>
          )}

          <div className="grid grid-cols-2 gap-3">
            <StatTile label="기관" value={formatCount(data.facilities.total)} sub={`운영 중 ${data.facilities.approved}`} />
            <StatTile label="사용자" value={formatCount(data.users)} sub={`원아 ${formatCount(data.children)}명`} />
            <StatTile
              label="활성 구독"
              value={formatCount(data.subscriptions.active)}
              sub={`30일 내 만료 ${data.subscriptions.expiringSoon}`}
            />
            <StatTile
              label="미해결 오류"
              value={formatCount(data.openErrors)}
              sub={data.openErrors > 0 ? '에러 탭에서 확인' : '깨끗해요'}
              to="/errors"
            />
          </div>

          <section>
            <SectionTitle>최근 7일 활동</SectionTitle>
            <Card>
              <div className="grid grid-cols-3 gap-2 text-center">
                <MiniStat label="알림장" value={data.activity7d.dailyNotes} />
                <MiniStat label="앨범" value={data.activity7d.albums} />
                <MiniStat label="쪽지" value={data.activity7d.messages} />
              </div>
            </Card>
          </section>

          <section>
            <SectionTitle>알림 파이프라인</SectionTitle>
            <Card className="flex flex-col gap-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <MiniStat label="24시간 생성" value={data.notifications.created24h} />
                <MiniStat label="발송 대기" value={data.notifications.pending} />
                <MiniStat label="실패 (7일)" value={data.notifications.failed7d} />
              </div>

              {data.notifications.recentFailures.length > 0 && (
                <div className="flex flex-col gap-2 border-t border-line pt-3">
                  <p className="text-sm font-semibold text-ink-sub">최근 실패</p>
                  {data.notifications.recentFailures.map((failure) => (
                    <div key={failure.id} className="rounded-sm bg-bg px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Chip tone="accent">{DELIVERY_CHANNEL_LABELS[failure.channel] ?? failure.channel}</Chip>
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {failure.notification.title}
                        </span>
                        <span className="shrink-0 text-ink-sub">{formatRelative(failure.updatedAt)}</span>
                      </div>
                      {failure.error && (
                        <p className="mt-1 break-all text-[13px] text-ink-sub">{failure.error}</p>
                      )}
                    </div>
                  ))}
                  <Link to="/system" className="text-sm font-semibold text-primary-strong underline">
                    시스템 탭에서 실패 재발송하기
                  </Link>
                </div>
              )}
            </Card>
          </section>

          <FeedbackInbox />

          <ReportInbox />

          {data.aiUsage.length > 0 && (
            <section>
              <SectionTitle>AI 사용량 (누적)</SectionTitle>
              <Card className="flex flex-col gap-2">
                {data.aiUsage.map((usage) => (
                  <div key={usage.feature} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{usage.feature}</span>
                    <span className="text-ink-sub">
                      {formatCount(usage._count)}회 · 토큰{' '}
                      {formatCount((usage._sum.inputTokens ?? 0) + (usage._sum.outputTokens ?? 0))}
                    </span>
                  </div>
                ))}
              </Card>
            </section>
          )}
        </div>
      )}
    </Screen>
  );
}

/** 이용자 건의함 (v1.18) — 앱 설정에서 보낸 불편·제안이 모인다. 매일 아침 메일로도 종합된다. */
function FeedbackInbox() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const { data } = useQuery({
    queryKey: ['admin', 'feedback', showAll ? 'all' : 'new'],
    queryFn: () =>
      apiData<AdminFeedback[]>(`/admin/feedback${showAll ? '' : '?status=NEW'}`),
    placeholderData: keepPreviousData,
  });

  const review = useMutation({
    mutationFn: (id: string) => apiData(`/admin/feedback/${id}/review`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
  });

  const items = data ?? [];

  return (
    <section>
      <SectionTitle
        action={
          <button
            type="button"
            className="text-sm font-semibold text-primary-strong"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? '새 건의만' : '전체 보기'}
          </button>
        }
      >
        이용자 건의함
      </SectionTitle>

      {items.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-sub">
            {showAll ? '아직 도착한 건의가 없어요.' : '새로 도착한 건의가 없어요.'}
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <Card key={item.id}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone="primary">{feedbackCategoryLabel(item.category)}</Chip>
                {item.facilityName && <Chip tone="neutral">{item.facilityName}</Chip>}
                {item.role && (
                  <Chip tone="neutral">
                    {ROLE_LABELS[item.role as keyof typeof ROLE_LABELS] ?? item.role}
                  </Chip>
                )}
                <span className="ml-auto text-sm text-ink-sub">{formatRelative(item.createdAt)}</span>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed">{item.content}</p>
              {item.status === 'NEW' && (
                <div className="mt-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    loading={review.isPending}
                    onClick={() => review.mutate(item.id)}
                  >
                    확인 처리
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

/** 신고 현황 (§5.30) — 원장이 처리하지만 운영진도 지켜본다. 여기서는 읽기만 한다 */
function ReportInbox() {
  const [showAll, setShowAll] = useState(false);
  const { data } = useQuery({
    queryKey: ['admin', 'reports', showAll ? 'all' : 'open'],
    queryFn: () => apiData<AdminReport[]>(`/admin/reports${showAll ? '' : '?status=OPEN'}`),
    placeholderData: keepPreviousData,
  });
  const items = data ?? [];

  return (
    <section>
      <SectionTitle
        action={
          <button
            type="button"
            className="text-sm font-semibold text-primary-strong"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? '대기 중만' : '전체 보기'}
          </button>
        }
      >
        신고 현황
      </SectionTitle>
      {items.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-sub">{showAll ? '접수된 신고가 없어요.' : '확인 대기 중인 신고가 없어요.'}</p>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <Card key={item.id}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone="primary">{item.targetLabel}</Chip>
                <Chip tone="accent">{item.reasonLabel}</Chip>
                <Chip tone={item.status === 'OPEN' ? 'secondary' : 'neutral'}>{item.status}</Chip>
                {item.facilityName && <Chip tone="neutral">{item.facilityName}</Chip>}
                <span className="ml-auto text-sm text-ink-sub">{formatRelative(item.createdAt)}</span>
              </div>
              {item.excerpt && <p className="mt-2 whitespace-pre-wrap text-sm">{item.excerpt}</p>}
              <p className="mt-1 text-sm text-ink-sub">
                신고 {item.reporterName}
                {item.reportedUserName ? ` · 대상 ${item.reportedUserName}` : ''}
                {item.action ? ` · ${item.action}` : ''}
              </p>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

interface AdminReport {
  id: string;
  targetLabel: string;
  reasonLabel: string;
  excerpt: string | null;
  status: 'OPEN' | 'RESOLVED' | 'DISMISSED';
  action: string | null;
  reporterName: string;
  reportedUserName: string | null;
  facilityName: string;
  createdAt: string;
}

interface AdminFeedback {
  id: string;
  facilityName: string | null;
  userName: string | null;
  role: string | null;
  category: string;
  content: string;
  status: 'NEW' | 'REVIEWED';
  createdAt: string;
}

function StatTile({
  label,
  value,
  sub,
  to,
}: {
  label: string;
  value: string;
  sub?: string;
  to?: string;
}) {
  const body = (
    <Card className="h-full">
      <p className="text-sm text-ink-sub">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sub && <p className="mt-0.5 text-sm text-ink-sub">{sub}</p>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-lg font-bold">{formatCount(value)}</p>
      <p className="text-sm text-ink-sub">{label}</p>
    </div>
  );
}
